//This file includes everything needed to call the server's web service API methods. The functions here all start with a word that's like a namespace and then an underscore, which denotes the controller on the server. For example, authentication_ValidateUser will call the API method ValidateUser that is part of the Authentication controller.
//There are a bunch of classes defined at the bottom of this file that are for sending data to and from the server with the API. They all have a counterpart on the server with the same name and properties.
import { Injectable, Inject } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { Observable, of } from 'rxjs';
import { MessageBoxComponent } from '../message-box/message-box.component';

declare var webApiBaseUrl: string;//This tells the compiler that it's a global variable coming from another file. In our case, it's coming from config.js.

@Injectable({
  providedIn: 'root'
})
export class WebService {
  //private baseURL: string; //Only use this if the baseURL for the web API is the same as for the UI (i.e. index.html). The baseURL string passed in comes from main.ts, which grabs the URL from the <base> element in index.html.

  constructor(private http: HttpClient, @Inject('BASE_URL') baseURL: string) {
    //this.baseURL = baseURL;
    if (!webApiBaseUrl)
      alert("config.js not loaded");
  }

  authentication_ValidateUser(userName: string, password: string, agency: string): Observable<ValidateUserResponse> {
    let requestParams = new ValidateUserRequest(userName, password, agency);
    return this.http.post<ValidateUserResponse>(webApiBaseUrl + 'api/Authentication/ValidateUser', requestParams);
  }

  authentication_GetMaskedEmail(userName: string, agency: string): Observable<GetStringResponse> {
    return this.http.post<GetStringResponse>(webApiBaseUrl + `api/Authentication/GetMaskedEmail?userName=${userName}&agency=${agency}`, null);
  }

  authentication_SendEmailToResetPassword(userName: string, agency: string): Observable<ApiResponse> {
    let requestParams = new ChangePasswordRequest(userName, '', '', agency);
    return this.http.post<ApiResponse>(webApiBaseUrl + 'api/Authentication/SendEmailToResetPassword', requestParams);
  }

  authentication_GetAllUsers(): Observable<GetUsersResponse> {
    return this.http.post<GetUsersResponse>(webApiBaseUrl + 'api/Authentication/GetAllUsers', null);
  }

  authentication_CreateUserFromEmployee(user: User): Observable<GetUsersResponse> {
    return this.http.post<GetUsersResponse>(webApiBaseUrl + 'api/Authentication/CreateUserFromEmployee', user);
  }

  authentication_UpdateUser(user: User): Observable<GetUsersResponse> {
    return this.http.post<GetUsersResponse>(webApiBaseUrl + 'api/Authentication/UpdateUser', user);
  }

  authentication_DeleteUser(user: User): Observable<GetUsersResponse> {
    return this.http.post<GetUsersResponse>(webApiBaseUrl + 'api/Authentication/DeleteUser', user);
  }

  authentication_ChangePasswordWithoutOldPassword(userName: string, newPassword: string, agency: string, guid: string): Observable<ApiResponse> {
    let requestParams = new ChangePasswordRequest(userName, '', newPassword, agency);
    return this.http.post<ApiResponse>(webApiBaseUrl + 'api/Authentication/ChangePasswordWithoutOldPassword?guid=' + guid, requestParams);
  }

  authentication_ChangePassword(userName: string, oldPassword: string, newPassword: string, agency: string): Observable<ApiResponse> {
    let requestParams = new ChangePasswordRequest(userName, oldPassword, newPassword, agency);
    return this.http.post<ApiResponse>(webApiBaseUrl + 'api/Authentication/ChangePassword', requestParams);
  }

  userProfiles_UploadPicture(employeeNo: number, files: FileList): Observable<Blob> {
    //The fileList will have the profile picture. Send it in the body of the request as form data.
    let formData = new FormData();
    formData.append('file', files[0], files[0].name);
    return this.http.post(webApiBaseUrl + 'api/UserProfiles/UploadProfileImageByEmployee?employeeNo=' + employeeNo, formData, { responseType: "blob" });
  }

  userProfiles_GetProfileImageNoToken(profileImageName: string): Observable<Blob> {
    return this.http.get(webApiBaseUrl + 'api/UserProfiles/GetProfileImageNoToken?profileImageName=' + profileImageName, { responseType: "blob" });
  }

  devices_GetAllDevices(): Observable<GetDevicesResponse> {
    return this.http.post<GetDevicesResponse>(webApiBaseUrl + 'api/Devices/GetAllDevices', null);
  }

  lookupOptions_GetReportFilterOptions(): Observable<GetReportFilterOptionsResponse> {
    return this.http.post<GetReportFilterOptionsResponse>(webApiBaseUrl + 'api/LookupOptions/GetReportFilterOptions', null);
  }

  lookupOptions_GetRoleOptions(): Observable<GetOptionsResponse> {
    return this.http.post<GetOptionsResponse>(webApiBaseUrl + 'api/LookupOptions/GetRoleOptions', null);
  }

  dashboard_GetAllData(combineAllDivisions: boolean): Observable<GetDashboardDataResponse> {
    return this.http.post<GetDashboardDataResponse>(webApiBaseUrl + 'api/Dashboard/GetAllData?combineAllDivisions=' + combineAllDivisions, null);
  }

  dashboard_GetEvaluationScoresDrilldown(selectedDivisionName: string, combineAllDivisions: boolean): Observable<GetEvaluationScoresDrilldownResponse> {
    return this.http.post<GetEvaluationScoresDrilldownResponse>(webApiBaseUrl + 'api/Dashboard/GetEvaluationScoresDrilldown?selectedDivisionName=' + selectedDivisionName + '&combineAllDivisions=' + combineAllDivisions, null);
  }

  dashboard_GetShiftsWithoutEvaluationsDrilldown(selectedDivisionName: string, combineAllDivisions: boolean): Observable<GetEmployeeShiftsDrilldownResponse> {
    return this.http.post<GetEmployeeShiftsDrilldownResponse>(webApiBaseUrl + 'api/Dashboard/GetShiftsWithoutEvaluationsDrilldown?selectedDivisionName=' + selectedDivisionName + '&combineAllDivisions=' + combineAllDivisions, null);
  }

  dashboard_GetFeedbackScoresDrilldown(selectedDivisionName: string, combineAllDivisions: boolean): Observable<GetEvaluationScoresDrilldownResponse> {
    return this.http.post<GetEvaluationScoresDrilldownResponse>(webApiBaseUrl + 'api/Dashboard/GetFeedbackScoresDrilldown?selectedDivisionName=' + selectedDivisionName + '&combineAllDivisions=' + combineAllDivisions, null);
  }

  dashboard_GetEvaluationsWithoutFeedbacksDrilldown(selectedDivisionName: string, combineAllDivisions: boolean): Observable<GetEvaluationScoresDrilldownResponse> {
    return this.http.post<GetEvaluationScoresDrilldownResponse>(webApiBaseUrl + 'api/Dashboard/GetEvaluationsWithoutFeedbacksDrilldown?selectedDivisionName=' + selectedDivisionName + '&combineAllDivisions=' + combineAllDivisions, null);
  }

  reports_GetEvaluationCoverageOverview(evaluationTypeNo: EvaluationType, filter: ReportFilter): Observable<GetReportDataResponse> {
    return this.http.post<GetReportDataResponse>(webApiBaseUrl + 'api/Reports/GetEvaluationCoverageOverview?evaluationTypeNo=' + evaluationTypeNo, filter);
  }
  
  reports_GetEvaluationCoverageDrilldown(evaluationTypeNo: EvaluationType, filter: ReportFilter): Observable<GetEvaluationCoverageDrilldownResponse> {
    return this.http.post<GetEvaluationCoverageDrilldownResponse>(webApiBaseUrl + 'api/Reports/GetEvaluationCoverageDrilldown?evaluationTypeNo=' + evaluationTypeNo, filter);
  }

  reports_GetEvaluationScoresOverview(evaluationTypeNo: EvaluationType, filter: ReportFilter): Observable<GetReportDataResponse> {
    return this.http.post<GetReportDataResponse>(webApiBaseUrl + 'api/Reports/GetEvaluationScoresOverview?evaluationTypeNo=' + evaluationTypeNo, filter);
  }
  
  reports_GetEvaluationScoresDrilldown(evaluationTypeNo: EvaluationType, filter: ReportFilter): Observable<GetEvaluationScoresDrilldownResponse> {
    return this.http.post<GetEvaluationScoresDrilldownResponse>(webApiBaseUrl + 'api/Reports/GetEvaluationScoresDrilldown?evaluationTypeNo=' + evaluationTypeNo, filter);
  }

  reports_GetEvaluationDetails(evaluationTypeNo: EvaluationType, filter: ReportFilter): Observable<GetEvaluationDetailsResponse> {
    return this.http.post<GetEvaluationDetailsResponse>(webApiBaseUrl + 'api/Reports/GetEvaluationDetails?evaluationTypeNo=' + evaluationTypeNo + '&startDate=' + filter.startDate + '&endDate=' + filter.endDate, null);
  }

  reports_GetEvaluationConsentOverview(filter: ReportFilter): Observable<GetReportDataResponse> {
    return this.http.post<GetReportDataResponse>(webApiBaseUrl + 'api/Reports/GetEvaluationConsentOverview', filter);
  }

  reports_GetEvaluationConsentDrilldown(filter: ReportFilter): Observable<GetEvaluationScoresDrilldownResponse> {
    return this.http.post<GetEvaluationScoresDrilldownResponse>(webApiBaseUrl + 'api/Reports/GetEvaluationConsentDrilldown', filter);
  }

  evaluations_GetEvaluations(evaluationTypeNo: EvaluationType): Observable<GetEvaluationsResponse> {
    return this.http.post<GetEvaluationsResponse>(webApiBaseUrl + 'api/Evaluations/GetEvaluations?evaluationTypeNo=' + evaluationTypeNo, null);
  }

  evaluations_AddEvaluation(evaluationTypeNo: EvaluationType, evaluationName: string): Observable<GetStringResponse> {
    return this.http.post<GetStringResponse>(webApiBaseUrl + 'api/Evaluations/AddEvaluation?evaluationTypeNo=' + evaluationTypeNo + '&evaluationName=' + evaluationName, null);
  }

  evaluations_EditEvaluation(evaluation: Evaluation): Observable<GetStringResponse> {
    return this.http.post<GetStringResponse>(webApiBaseUrl + 'api/Evaluations/EditEvaluation', evaluation);
  }

  evaluations_DeleteEvaluation(evaluationTypeNo: EvaluationType, evaluationNo: number): Observable<GetEvaluationsResponse> {
    return this.http.post<GetEvaluationsResponse>(webApiBaseUrl + 'api/Evaluations/DeleteEvaluation?evaluationTypeNo=' + evaluationTypeNo + '&evaluationNo=' + evaluationNo, null);
  }

  //This web API actually returns a class called GetPublishedEvaluationQuestionsResponse, but it's almost identical to GetWorkingEvaluationQuestionsResponse, and it makes our life easier to consider it to be GetWorkingEvaluationQuestionsResponse.
  evaluations_GetPublishedEvaluationQuestions(evaluationNo: number): Observable<GetWorkingEvaluationQuestionsResponse> {
    return this.http.post<GetWorkingEvaluationQuestionsResponse>(webApiBaseUrl + 'api/Evaluations/GetPublishedEvaluationQuestions?evaluationNo=' + evaluationNo, null);
  }

  evaluations_GetWorkingEvaluationQuestions(evaluationNo: number): Observable<GetWorkingEvaluationQuestionsResponse> {
    return this.http.post<GetWorkingEvaluationQuestionsResponse>(webApiBaseUrl + 'api/Evaluations/GetWorkingEvaluationQuestions?evaluationNo=' + evaluationNo, null);
  }

  evaluations_AddEditQuestion(evaluationNo: number, question: EvaluationQuestion): Observable<GetWorkingEvaluationQuestionsResponse> {
    return this.http.post<GetWorkingEvaluationQuestionsResponse>(webApiBaseUrl + 'api/Evaluations/AddEditQuestion?evaluationNo=' + evaluationNo, question);
  }

  evaluations_EditQuestionOrder(evaluationNo: number, questionAssignmentNo: number, moveLower: boolean): Observable<GetWorkingEvaluationQuestionsResponse> {
    return this.http.post<GetWorkingEvaluationQuestionsResponse>(webApiBaseUrl + 'api/Evaluations/EditQuestionOrder?evaluationNo=' + evaluationNo + '&questionAssignmentNo=' + questionAssignmentNo + '&moveLower=' + moveLower, null);
  }

  //This web API actually returns a class called GetPublishedEvaluationQuestionsResponse, but it's almost identical to GetWorkingEvaluationQuestionsResponse, and it makes our life easier to consider it to be GetWorkingEvaluationQuestionsResponse.
  evaluations_PublishQuestions(evaluationNo: number): Observable<any> {
    return this.http.post(webApiBaseUrl + 'api/Evaluations/PublishQuestions?evaluationNo=' + evaluationNo, null);
  }

  systemSetting_GetSystemSettings(): Observable<GetSystemSettingsResponse> {
    return this.http.post<GetSystemSettingsResponse>(webApiBaseUrl + 'api/SystemSetting/GetSystemSettings', null);
  }

  evaluations_UpdateSystemSettings(systemSettings: SystemSettings): Observable<null> {
    return this.http.post<null>(webApiBaseUrl + 'api/SystemSetting/UpdateSystemSettings', systemSettings);
  }
}

//base class for response objects
export class ApiResponse {
  errorMessage: string;
  errorCode: number;

  static checkStatus(resp: ApiResponse): boolean {
    if (resp.errorCode != 0) {
      if (MessageBoxComponent.staticInstance)
        MessageBoxComponent.staticInstance.show("Error", resp.errorMessage, false);
      return false;
    }
    return true;
  }
}

export class LoginInfo {
  userName: string;
  authString: string;
}

export class NameInfo {
  public firstName: string;
  public lastName: string;
  public middleName: string;
  public prefix: string;
  public suffix: string;
}

export class ValidateUserResponse extends ApiResponse {
  userValidated: boolean;
  passwordExpired: boolean;
  userName: string;
  userEmployeeNo: number;
  userInfo: NameInfo;
  validationToken: string;
  authorityRoles: string[];
  configInfo: string[][];
  daysUntilPasswordExpires: number;
  homeDivisionNo: number;
}

export class ValidateUserRequest {
  constructor( //These params will automatically become the properties of this class
    public userName: string,
    public password: string,
    public agency: string
  ) { };
}

export class ChangePasswordRequest {
  constructor( //These params will automatically become the properties of this class
    public username: string,
    public oldPassword: string,
    public newPassword: string,
    public agency: string
  ) { };
}

export class User {
  constructor( //These params will automatically become the properties of this class
    public employeeNo: number,
    public userName: string,
    public firstName: string,
    public lastName: string,
    public employeePosition: string,
    public employmentStartDate: Date,
    public homeDivision: string,
    public isLockedOut: boolean,
    public profileImageName: string,
    public profileImageContent: any, //This doesn't get filled in when we get the list of records. This might get filled in later one by one.
    //public lastLogin: Date,
    public userRoles: string,
    public password: string,
    public canViewCompletedEvals: boolean,
    public email: string,
  ) { };

  static create() {
    return new User(0, '', '', '', '', null, '', false, '', null, '', '', false, '');
  }
}

export class GetUsersResponse extends ApiResponse {
  public users: User[];
}

export class GetStringResponse extends ApiResponse {
  public message: string;
}

export class Device {
  public deviceRegistrationID: string;
  public osVersion: string;
  public model: string;
  public brand: string;
  public serialNumber: string;
  public lastLoginLoweredUserName: string;
  public mobileAppVersion: string;
  public lastLoginDate: Date;
  public createdDate: Date;
}

export class GetDevicesResponse extends ApiResponse {
  public devices: Device[];
}

export class LookupOption {
  optionId: string;
  optionName: string;
}

export class GetReportFilterOptionsResponse extends ApiResponse {
  divisionsList: LookupOption[];
  supervisorsList: LookupOption[];
  shiftsList: LookupOption[];
  positionsList: LookupOption[];
}

export class GetOptionsResponse extends ApiResponse {
  optionsList: LookupOption[];
}

export class GetDashboardDataResponse extends ApiResponse {
  public chartData1: Object[][];
  public chartData2: Object[][];
  public chartData3: Object[][];
  public chartData4: Object[][];
  public chartData5: Object[][];
  public chartData6: Object[][];
  public chartData7: Object[][];
  public chartData8: Object[][];
}

export class GetEmployeeShiftsDrilldownResponse extends ApiResponse {
  public shifts: EmployeeShift[];
}

export class EmployeeShift {
  public employeeName: string;
  public supervisorName: string;
  public shiftStartTime: Date;
  public shiftEndTime: Date;
}

export class ReportFilter {
  startDate: string;
  endDate: string;
  dateGroupingNo: string;
  tenureNo: string;
  divisionNo: string;
  supervisorNo: string;
  shiftNo: string;
  positionNo: string;
  selectedDivisionName: string;
  selectedDateGroupName: string
}

export class GetReportDataResponse extends ApiResponse {
  public chartData: Object[][];
}

export class EvaluationCoverage {
  public employeeName: string;
  public fractionTop: number;
  public fractionBottom: number;
  public coveragePercent: number;
}

export class GetEvaluationCoverageDrilldownResponse extends ApiResponse {
  public coverages: EvaluationCoverage[];
}

export class EvaluationScore {
  public employeeName: string;
  public supervisorName: string;
  public evaluationTime: Date;
  public totalScore: number;
}

export class GetEvaluationScoresDrilldownResponse extends ApiResponse {
  public scores: EvaluationScore[];
}

export class GetEvaluationDetailsResponse extends ApiResponse {
  public details: any[]; //We don't define this class because it will have variable properties from the server.
}

export class Evaluation {
  constructor( //These params will automatically become the properties of this class
    public evaluationNo: number,
    public evaluationName: string,
    public updatedTime: string, //this is a UTC time in the format "2019-08-13T08:42:45Z" when it comes in a list from the server
    public publishedTime: string, //this is a UTC time in the format "2019-08-13T08:42:45Z" when it comes in a list from the server. This will be blank if it doesn't exist.
    public questionsUpdatedTime: string, //this is a UTC time in the format "2019-08-13T08:42:45Z" when it comes in a list from the server. This will be blank if it doesn't exist.
    public numPublishedQuestions: number,
    public evaluationPositions: string,
    public questionChangeSincePublishing: boolean //this does not come from the server, but we calculate it
  ) { };
}

export class GetEvaluationsResponse extends ApiResponse {
  public evaluations: Evaluation[];
}

export class EvaluationQuestion {
  constructor( //These params will automatically become the properties of this class
    public questionNo: number,
    public questionAssignmentNo: number,
    public questionOrder: number,
    public questionText: string,
    public questionActive: boolean,
    public questionWeight: number,
    public questionLocked: boolean
  ) { };

  static create() {
    return new EvaluationQuestion(0, 0, 0, '', true, 1, false);
  }
}

export class GetWorkingEvaluationQuestionsResponse extends ApiResponse {
  evaluationQuestions: EvaluationQuestion[];
  evaluationNo: number;
}

export class GetSystemSettingsResponse extends ApiResponse {
  settings: SystemSettings;
}

export class SystemSettings {
  constructor(
    public allowPushNotification: boolean = false,
    public delayPushNotificationMins: string = "",
    public notifySupervisorsAboutFeedback: boolean = false,
    public notifySupervisorsAboutLogin: boolean = false,
    public notifySupervisorsAboutLoginMinutes: string = "",
    public notifySupervisorsAboutLoginRepeat: string = "",
    public notifySupervisorsAboutLoginInterval: string = "",
    public notifySupervisorsAboutEncounter: boolean = false,
    public notifySupervisorsAboutEncounterMinutes: string = "",
    public notifySupervisorsAboutEncounterRepeat: string = "",
    public notifySupervisorsAboutEncounterInterval: string = "",
    public notifySupervisorsAboutNewEmployeeMilestones: boolean = false,
    public notifyEmployeeAboutNewEmployeeMilestones: boolean = false,
  ) { };
}

export enum EvaluationType {
  Supervisor = 1,
  Employee = 2
}

export class Formatter {
  //This function will take a date object or date string and convert it into a date/time that is consistent throughout the system. This function is often used as the cellRenderer for a grid column; in this case the object passed in is of type ICellRendererParams, so we have to use param.value as the date string.
  static ToDateTime(param): string {
    if (param.column)
      param = param.value; // param is of type ICellRendererParams

    if (!param || (typeof param == "string" && param.trim() == ""))
      return "";

    let options = { year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: 'numeric', hour12: false };
    return (new Date(param)).toLocaleString("en-US", options);
  }

  //This function will take a date object or date string and convert it into a date that is consistent throughout the system. This function is often used as the cellRenderer for a grid column; in this case the object passed in is of type ICellRendererParams, so we have to use param.value as the date string.
  static ToDate(param): string {
    if (param.column)
      param = param.value; // param is of type ICellRendererParams

    if (!param || (typeof param == "string" && param.trim() == ""))
      return "";

    let options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    return (new Date(param)).toLocaleString("en-US", options);
  }

  //This is used to set the max y-axis value in the chart control we use. If there are numbers in the array greater than 0, then we return null and allow the chart control to decide on an appropriate max value for the y-axis. However, if there are no numbers greater than 0 and we return null, it will go from -1 to 1, which we don't want, so in that case, we return 100.
  static GetMaxYAxis(arr: any[]): number {
    let max: number = this.GetMaxArrayValue(arr);
    return (max > 0 ? null : 100);
  }

  //this will return the max value in an array. If the array has nested arrays, it will search through each one. It will return 0 if no numbers are found.
  static GetMaxArrayValue(arr: any[]): number {
    let max: number = 0;
    //Go through every item in the array. If the item is an array, call this funtion recursively. If the item is a number, compare it to max.
    for (let i = 0; i < arr.length; i++) {
      if (Array.isArray(arr[i]))
        max = Math.max(max, this.GetMaxArrayValue(arr[i]));
      else if (!isNaN(arr[i]))
        max = Math.max(max, arr[i]);
    }
    return max;
  }

  //returns true only if the passed-in string is a valid email format.
  static validateEmail(str): boolean {
    if (!str)
      return false;
    var pattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return pattern.test(str.toLowerCase());
  }
}
